'use client';

import { useAuth } from '@/context/AuthContext';
import useUpdateUser from '@/context/useUpdateUser';
import { useImpersonation } from '@/hooks/useImpersonation';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { hasActivePremiumPlan } from '@/lib/query';
import { isDev } from '@/lib/server';
import { api } from '@/lib/trpc/react';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import * as Sentry from '@sentry/nextjs';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

export function DeviceChecker() {
  const { user, userLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { track } = useAnalytics();
  const { checkAdmin } = useImpersonation();
  const { updateUser } = useUpdateUser();
  const { getData } = useVisitorData(
    { extendedResult: true },
    {
      immediate: false,
    },
  );

  const { mutateAsync: addDeviceMutation } =
    api.fingerprint.addDevice.useMutation();

  useEffect(() => {
    if (userLoading || isDev) return;

    const checkDevice = searchParams?.get('checkDevice');
    if (checkDevice !== 'true') return;

    if ((user && !hasActivePremiumPlan(user)) || !user) {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.delete('checkDevice');
      const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      router.replace(newUrl);
      return;
    }

    const verifyFingerprint = async () => {
      const admin = await checkAdmin(user.email);
      if (admin) return;
      const verifiedData = await getData();
      if (!verifiedData.visitorId) {
        Sentry.captureMessage('No visitorId found');
        return;
      }

      if (!user.uniqueVisitorId) {
        await updateUser({
          uniqueVisitorId: verifiedData.visitorId,
          email: user.email,
        });
      }

      const matchingDevice = user.devices.find(
        (device: { uniqueVisitorId: string }) =>
          device.uniqueVisitorId === verifiedData.visitorId,
      );

      if (matchingDevice && !matchingDevice.exceedsLimit) return;

      if (matchingDevice?.exceedsLimit) {
        track('Account Sharing Detected', {
          userId: user.id,
          email: user.email,
          uniqueVisitorId: verifiedData.visitorId,
        });
        return;
      }

      const deviceName = `${verifiedData.browserName}-${verifiedData.os}-${verifiedData.device}`;

      const combinations = [
        ...new Set(
          user.devices.map(
            (device) => `${device.userId}-${device.uniqueVisitorId}`,
          ),
        ),
      ];
      track('new_device_added', {
        userId: user.id,
        email: user.email,
        uniqueVisitorId: verifiedData.visitorId,
        deviceName,
        exceedsLimit: combinations.length >= 3,
      });
      addDeviceMutation({
        uniqueVisitorId: verifiedData.visitorId,
        name: deviceName,
        exceedsLimit: combinations.length >= 3,
      });

      if (combinations.length < 3) return;

      track('Account Sharing Detected', {
        userId: user.id,
        email: user.email,
        uniqueVisitorId: verifiedData.visitorId,
      });
    };

    verifyFingerprint().finally(() => {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.delete('checkDevice');
      const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      router.replace(newUrl);
    });
  }, [
    user,
    searchParams,
    userLoading,
    pathname,
    router,
    checkAdmin,
    getData,
    updateUser,
    track,
    addDeviceMutation,
  ]);

  return null;
}
